import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Translate } from 'react-localize-redux';
import css from './TabGroupedErrors.css';

// Each error translation renders `<span>Label:</span> rest of message` via
// dangerouslySetInnerHTML (the Translate component wraps that in its own span),
// so the prefix span is the first element child of the Translate's wrapper.
// We delegate clicks from the outer wrapper and only fire when the click target
// is inside that prefix span — also keeps jsx-a11y quiet about JSX onClick on
// non-interactive elements.

const TabGroupedErrors = ({ tabs, getTabErrors, onErrorClick }) => {
    const listRef = useRef(null);

    useEffect(() => {
        if (!onErrorClick) return undefined;
        const node = listRef.current;
        if (!node) return undefined;
        const onClick = e => {
            const li = e.target.closest?.('li[data-error-key]');
            if (!li || !node.contains(li)) return;
            const wrapper = li.firstElementChild;
            const prefix = wrapper?.firstElementChild;
            if (!prefix) return;
            if (e.target !== prefix && !prefix.contains(e.target)) return;
            onErrorClick(li.dataset.errorTabId, li.dataset.errorKey);
        };
        node.addEventListener('click', onClick);
        return () => node.removeEventListener('click', onClick);
    }, [onErrorClick]);

    return (
        <div ref={listRef} className={css.submitErrorList}>
            {tabs
                ?.map(tab => {
                    const errors = getTabErrors(tab);
                    if (!errors.length) return null;
                    return (
                        <div key={tab.id}>
                            <div className={css.submitErrorTabTitle}>
                                <Translate id={tab.label} />
                            </div>
                            <ul>
                                {errors.map(({ key, messageKey, data }) => (
                                    <li
                                        key={key}
                                        className={css.errorItem}
                                        data-error-tab-id={tab.id}
                                        data-error-key={key}
                                    >
                                        <Translate id={messageKey} data={data} />
                                    </li>
                                ))}
                            </ul>
                        </div>
                    );
                })
                .filter(Boolean)}
        </div>
    );
};

TabGroupedErrors.propTypes = {
    tabs: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
        })
    ),
    getTabErrors: PropTypes.func.isRequired,
    onErrorClick: PropTypes.func,
};

export default TabGroupedErrors;
